import { UnifiedUser } from "@/types/unified-user";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, Calendar, Monitor, Shield } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface UserActivityTabProps {
  user: UnifiedUser;
}

export function UserActivityTab({ user }: UserActivityTabProps) {
  // Mock activity data - in real app, this would come from API
  const activities = [
    ...(user.lastActive ? [{
      id: '1',
      type: 'login',
      description: 'Last active',
      timestamp: user.lastActive,
      icon: Activity,
    }] : []),
    ...(user.syncedAt ? [{
      id: '2',
      type: 'sync',
      description: 'Directory sync',
      timestamp: user.syncedAt,
      icon: Calendar,
    }] : []),
    ...(user.deviceCount > 0 ? [{
      id: '3',
      type: 'device',
      description: `${user.deviceCount} device(s) enrolled`,
      timestamp: user.lastActive || new Date().toISOString(),
      icon: Monitor,
    }] : []),
    ...(user.roles && user.roles.length > 0 ? [{
      id: '4',
      type: 'role',
      description: `Assigned ${user.roles.length} role(s)`,
      timestamp: user.joinDate || new Date().toISOString(),
      icon: Shield,
    }] : []),
  ];

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Activity className="w-12 h-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Activity</h3>
        <p className="text-muted-foreground max-w-sm">
          No recent activity to display for this user.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
      
      <div className="space-y-3">
        {activities.map((activity) => {
          const Icon = activity.icon;
          return (
            <Card key={activity.id}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-muted p-2">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{activity.description}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {activity.type}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
